'use client';
import React, { useState } from 'react';
import { CheckCircle, XCircle, Clock, Eye, FileText, Calendar, Globe, RefreshCw, AlertTriangle } from 'lucide-react';

interface KYCEntry {
  id: string;
  userId: string;
  name: string;
  email: string;
  country: string;
  docType: string;
  submittedAt: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected';
  riskLevel: 'low' | 'medium' | 'high';
  documents: number;
  notes: string;
}

const kycQueue: KYCEntry[] = [
  { id: 'KYC-8821', userId: 'USR-48291', name: 'Arjun Kumar', email: 'arjun@example.com', country: 'India', docType: 'Aadhaar + PAN', submittedAt: '2h ago', status: 'pending', riskLevel: 'low', documents: 2, notes: '' },
  { id: 'KYC-8820', userId: 'USR-31204', name: 'Sarah Mitchell', email: 'sarah@example.com', country: 'USA', docType: 'Passport', submittedAt: '4h ago', status: 'under_review', riskLevel: 'low', documents: 1, notes: 'Passport expiry check needed' },
  { id: 'KYC-8819', userId: 'USR-22891', name: 'Carlos Mendez', email: 'carlos@example.com', country: 'Spain', docType: 'National ID', submittedAt: '6h ago', status: 'pending', riskLevel: 'medium', documents: 2, notes: 'Fraud score elevated' },
  { id: 'KYC-8818', userId: 'USR-19034', name: 'Priya Sharma', email: 'priya@example.com', country: 'India', docType: 'Aadhaar + PAN', submittedAt: '8h ago', status: 'approved', riskLevel: 'low', documents: 2, notes: '' },
  { id: 'KYC-8817', userId: 'USR-15892', name: 'James Wilson', email: 'james@example.com', country: 'UK', docType: 'Driving License', submittedAt: '1d ago', status: 'rejected', riskLevel: 'high', documents: 1, notes: 'Document appears altered. Refer to compliance.' },
  { id: 'KYC-8816', userId: 'USR-12034', name: 'Fatima Al-Hassan', email: 'fatima@example.com', country: 'UAE', docType: 'Emirates ID', submittedAt: '1d ago', status: 'pending', riskLevel: 'low', documents: 1, notes: '' },
  { id: 'KYC-8815', userId: 'USR-09821', name: 'Emma Thompson', email: 'emma@example.com', country: 'Australia', docType: 'Passport', submittedAt: '2d ago', status: 'under_review', riskLevel: 'medium', documents: 2, notes: 'Address verification pending' },
];

const statusConfig = {
  pending: { label: 'Pending', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: Clock },
  under_review: { label: 'Under Review', color: '#06B6D4', bg: 'rgba(6,182,212,0.12)', icon: Eye },
  approved: { label: 'Approved', color: '#10B981', bg: 'rgba(16,185,129,0.12)', icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#F43F5E', bg: 'rgba(244,63,94,0.12)', icon: XCircle },
};

const riskConfig = {
  low: { color: '#10B981', label: 'Low Risk' },
  medium: { color: '#F59E0B', label: 'Medium Risk' },
  high: { color: '#F43F5E', label: 'High Risk' },
};

export default function AdminKYCContent() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<KYCEntry | null>(null);

  const filtered = kycQueue.filter(k => statusFilter === 'all' || k.status === statusFilter);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">KYC Verification Queue</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Review and process identity verification requests</p>
        </div>
        <button className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Pending Review', value: '156', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: Clock },
          { label: 'Under Review', value: '34', color: '#06B6D4', bg: 'rgba(6,182,212,0.12)', icon: Eye },
          { label: 'Approved Today', value: '89', color: '#10B981', bg: 'rgba(16,185,129,0.12)', icon: CheckCircle },
          { label: 'Rejected Today', value: '12', color: '#F43F5E', bg: 'rgba(244,63,94,0.12)', icon: XCircle },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: s.bg }}>
              <s.icon size={16} style={{ color: s.color }} />
            </div>
            <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-2 p-1 rounded-xl w-fit" style={{ background: 'var(--muted)' }}>
        {[
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'under_review', label: 'Under Review' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
        ].map((tab) => (
          <button key={tab.value} onClick={() => setStatusFilter(tab.value)}
            className="text-xs px-4 py-2 rounded-lg font-semibold transition-all"
            style={{
              background: statusFilter === tab.value ? 'var(--card)' : 'transparent',
              color: statusFilter === tab.value ? 'var(--foreground)' : 'var(--muted-foreground)',
              boxShadow: statusFilter === tab.value ? '0 1px 4px rgba(0,0,0,0.3)' : 'none',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* KYC Queue */}
      <div className="space-y-3">
        {filtered.map((entry) => {
          const sc = statusConfig[entry.status];
          const rc = riskConfig[entry.riskLevel];
          const isSelected = selected?.id === entry.id;
          return (
            <div key={entry.id} className="rounded-xl overflow-hidden"
              style={{ background: 'var(--card)', border: `1px solid ${isSelected ? 'rgba(124,58,237,0.5)' : 'var(--border)'}` }}>
              <div className="p-4 cursor-pointer" onClick={() => setSelected(isSelected ? null : entry)}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
                    {entry.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground">{entry.name}</span>
                      <span className="text-xs font-mono" style={{ color: '#A78BFA' }}>{entry.id}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                      <span className="text-xs font-semibold" style={{ color: rc.color }}>{rc.label}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        <Globe size={10} />{entry.country}
                      </span>
                      <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        <FileText size={10} />{entry.docType}
                      </span>
                      <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        <Calendar size={10} />Submitted {entry.submittedAt}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{entry.documents} document(s)</span>
                    </div>
                    {entry.notes && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs p-2 rounded-lg"
                        style={{ background: 'rgba(245,158,11,0.08)', color: '#F59E0B' }}>
                        <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                        {entry.notes}
                      </div>
                    )}
                  </div>
                  <sc.icon size={18} style={{ color: sc.color, flexShrink: 0 }} />
                </div>
              </div>

              {isSelected && (
                <div className="px-4 pb-4 pt-0 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div className="pt-3 flex items-center gap-2 flex-wrap">
                    <button className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5">
                      <Eye size={12} /> Review Documents
                    </button>
                    <button className="text-xs py-1.5 px-3 rounded-lg font-semibold flex items-center gap-1.5 transition-colors hover:opacity-80"
                      style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
                      <CheckCircle size={12} /> Approve
                    </button>
                    <button className="text-xs py-1.5 px-3 rounded-lg font-semibold flex items-center gap-1.5 transition-colors hover:opacity-80"
                      style={{ background: 'rgba(244,63,94,0.15)', color: '#F43F5E' }}>
                      <XCircle size={12} /> Reject
                    </button>
                    <button className="text-xs py-1.5 px-3 rounded-lg font-semibold flex items-center gap-1.5 transition-colors hover:opacity-80"
                      style={{ background: 'rgba(6,182,212,0.15)', color: '#06B6D4' }}>
                      <Clock size={12} /> Request More Docs
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
